/**
 * API Key Detail Modal Component
 *
 * Modal for viewing API key details and rotation history.
 * Provides comprehensive view of key configuration with tabs.
 */

import { useState } from 'react';
import { Loader2, RefreshCw, Settings2, Trash2, Clock, History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useApiKeyById, useRotationHistory } from '@/hooks/use-api-keys';
import { ApiKeyRotateDialog } from './api-key-rotate-dialog';
import { ApiKeyDeleteDialog } from './api-key-delete-dialog';
import { ApiKeyRateLimitsDialog } from './api-key-rate-limits-dialog';
import type { ClientType } from '@/types/api-key';

interface ApiKeyDetailModalProps {
  keyId: string | null;
  onClose: () => void;
}

/**
 * Format Date
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
}

/**
 * Format Rate Limit
 */
function formatRateLimit(maxRequests: number, windowMs: number): string {
  const windowMinutes = Math.round(windowMs / 60000);
  return `${maxRequests.toLocaleString()} requests per ${windowMinutes} minute${windowMinutes !== 1 ? 's' : ''}`;
}

/**
 * Get Client Type Badge Variant
 */
function getClientTypeBadgeVariant(clientType: ClientType): 'default' | 'secondary' {
  return clientType === 'mobile' ? 'default' : 'secondary';
}

/**
 * Get Expiration Status
 */
function getExpirationInfo(expiresAt: string | null): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  daysLeft: number | null;
} {
  if (!expiresAt) {
    return { label: 'No Expiry', variant: 'outline', daysLeft: null };
  }

  const now = new Date();
  const expiry = new Date(expiresAt);
  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) {
    return { label: 'Expired', variant: 'destructive', daysLeft: daysUntilExpiry };
  } else if (daysUntilExpiry <= 7) {
    return { label: 'Expiring Soon', variant: 'destructive', daysLeft: daysUntilExpiry };
  } else if (daysUntilExpiry <= 30) {
    return { label: 'Expiring', variant: 'secondary', daysLeft: daysUntilExpiry };
  }
  return { label: 'Valid', variant: 'default', daysLeft: daysUntilExpiry };
}

/**
 * API Key Detail Modal
 * Renders modal with details and rotation history tabs
 */
export function ApiKeyDetailModal({ keyId, onClose }: ApiKeyDetailModalProps) {
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rateLimitsDialogOpen, setRateLimitsDialogOpen] = useState(false);

  const { data: apiKey, isLoading } = useApiKeyById(keyId);
  const { data: rotationHistory, isLoading: isLoadingHistory } = useRotationHistory(keyId);

  // Get expiration info
  const expirationInfo = apiKey ? getExpirationInfo(apiKey.expiresAt) : null;

  return (
    <>
      <Dialog open={keyId !== null} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">API Key Details</DialogTitle>
            <DialogDescription>{apiKey?.keyName || 'Loading...'}</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : apiKey ? (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="history">Rotation History</TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6 mt-4">
                {/* Status Row */}
                <div className="flex gap-2 flex-wrap">
                  <Badge variant={apiKey.isActive ? 'default' : 'secondary'}>
                    {apiKey.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant={getClientTypeBadgeVariant(apiKey.clientType)}>
                    {apiKey.clientType.toUpperCase()}
                  </Badge>
                  {expirationInfo && (
                    <Badge variant={expirationInfo.variant}>
                      {expirationInfo.label}
                      {expirationInfo.daysLeft !== null && expirationInfo.daysLeft > 0 && (
                        <span className="ml-1">({expirationInfo.daysLeft} days)</span>
                      )}
                    </Badge>
                  )}
                </div>

                <Separator />

                {/* Key Information */}
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Key Name</p>
                      <p className="font-medium">{apiKey.keyName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Key ID</p>
                      <p className="font-mono text-sm">{apiKey.id}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Client Type</p>
                      <p className="font-medium">{apiKey.clientType.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="font-medium">{apiKey.isActive ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Rate Limits */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Rate Limits</h4>
                  <div className="bg-muted p-4 rounded-md space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Max Requests:</span>
                      <span className="font-mono">{apiKey.maxRequests.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Time Window:</span>
                      <span className="font-mono">
                        {Math.round(apiKey.windowMs / 60000)} minutes (
                        {apiKey.windowMs.toLocaleString()} ms)
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-medium pt-2 border-t">
                      <span>Rate:</span>
                      <span>{formatRateLimit(apiKey.maxRequests, apiKey.windowMs)}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Timestamps */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Timestamps</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Created</p>
                      <p className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(apiKey.createdAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Last Updated</p>
                      <p className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(apiKey.updatedAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Last Used</p>
                      <p className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(apiKey.lastUsed)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Expires</p>
                      <p className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(apiKey.expiresAt)}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setRateLimitsDialogOpen(true)}>
                    <Settings2 className="h-4 w-4 mr-2" />
                    Rate Limits
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRotateDialogOpen(true)}
                    disabled={!apiKey.isActive}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Rotate Key
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={!apiKey.isActive}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Deactivate
                  </Button>
                </div>
              </TabsContent>

              {/* Rotation History Tab */}
              <TabsContent value="history" className="mt-4">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : rotationHistory && rotationHistory.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <History className="h-4 w-4" />
                      <span>{rotationHistory.length} rotation(s) recorded</span>
                    </div>
                    <div className="space-y-3">
                      {rotationHistory.map(item => (
                        <div key={item.id} className="bg-muted p-4 rounded-md space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm">Key Rotated</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(item.rotatedAt)}
                              </p>
                            </div>
                            <Badge variant="outline">{item.rotationReason}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Old Key ID: </span>
                              <span className="font-mono">{item.oldKeyId.slice(0, 8)}...</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">New Key ID: </span>
                              <span className="font-mono">{item.newKeyId.slice(0, 8)}...</span>
                            </div>
                          </div>
                          {item.rotatedBy && (
                            <p className="text-xs text-muted-foreground">
                              Rotated by: {item.rotatedBy}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <History className="h-8 w-8 mb-2" />
                    <p className="text-sm">No rotation history</p>
                    <p className="text-xs">This key has never been rotated</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              API key not found
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rotate Dialog */}
      {apiKey && (
        <ApiKeyRotateDialog
          apiKey={rotateDialogOpen ? apiKey : null}
          onClose={() => setRotateDialogOpen(false)}
        />
      )}

      {/* Delete Dialog */}
      {apiKey && (
        <ApiKeyDeleteDialog
          apiKey={deleteDialogOpen ? apiKey : null}
          onClose={() => setDeleteDialogOpen(false)}
        />
      )}

      {/* Rate Limits Dialog */}
      {apiKey && (
        <ApiKeyRateLimitsDialog
          apiKey={rateLimitsDialogOpen ? apiKey : null}
          onClose={() => setRateLimitsDialogOpen(false)}
        />
      )}
    </>
  );
}
